import React, { useEffect, useRef, useState } from "react";
import styles from "./chat-box.module.css";
import { DEFAULT_PROFILE_IMAGE } from "../../../../../../constants/common";
import { useChat } from "../../../../../../context/chat-context";
import dayjs from "dayjs";
import ChatInputSection from "./components/chat-input-section/chat-input-section";
import ChatAttachments from "./components/chat-attachments/chat-attachments";
import { Empty } from "antd";

const ChatBox = ({ selectedChat, onSend, chatRoles }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { messages, hasMore, page, setLoadMore, remainingMessages } = useChat();
  const containerRef = useRef(null);
  const bottomRef = useRef(null);
  const prevHeightRef = useRef(0);
  const chatPermission = chatRoles?.includes(selectedChat?.user_role);
  const disableChat = !remainingMessages || !chatPermission;

  useEffect(() => {
    if (page === 1) {
      bottomRef.current?.scrollIntoView({ behavior: "auto" });
    } else if (containerRef.current && prevHeightRef.current) {
      const container = containerRef.current;
      const heightDiff = container.scrollHeight - prevHeightRef.current;
      container.scrollTop = heightDiff;
    }
    setIsLoading(false);
  }, [messages]);

  const handleScroll = (e) => {
    const element = e.currentTarget;
    if (element.scrollTop <= 100 && hasMore && !isLoading) {
      setIsLoading(true);
      setLoadMore((prev) => prev + 1);
      prevHeightRef.current = element.scrollHeight;
    }
  };

  return (
    <div className={styles["chat-wrapper"]}>
      {disableChat && page ? (
        <div className={styles["chat-disabled-warning"]}>
          {!chatPermission ? (
            <p>{"You don’t have permission to chat with this user."}</p>
          ) : !remainingMessages ? (
            <p>{"You’ve reached your daily limit of 100 messages."}</p>
          ) : null}
        </div>
      ) : null}

      <div
        className={styles["chat-messages"]}
        ref={containerRef}
        onScroll={handleScroll}
      >
        <div className={styles["chat-group-container"]}>
          {messages?.length === 0 && (
            <Empty 
              description="No messages yet"
              style={{ margin: "auto" }}
            />
          )}
          {Array.isArray(messages) &&
            messages.map((values, index) => (
              <div
                key={`${index}-${values?.date}`}
                className={styles["chat-message-group"]}
              >
                {values?.messages?.map((msg, idx) => {
                  const fromMe = selectedChat?.user_id === msg?.receiver_id;
                  return (
                    <div
                      key={`${idx}-${msg?.message_id}`}
                      className={`${styles["chat-message"]} ${
                        styles[fromMe ? "from-me" : "from-them"]
                      }`}
                    >
                      <img
                        src={msg?.sender_profile_url || DEFAULT_PROFILE_IMAGE}
                        height={36}
                        width={36}
                        alt="avatar"
                        className={styles["chat-avatar"]}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = DEFAULT_PROFILE_IMAGE;
                        }}
                      />
                      <div className={styles["chat-bubble"]}>
                        {msg?.content && (
                          <div className={styles["chat-text"]}>
                            {msg?.content}
                          </div>
                        )}
                        {Array.isArray(msg?.attachment_urls) &&
                          msg?.attachment_urls?.filter(
                            (file) => file?.url && file?.name
                          )?.length > 0 && (
                            <ChatAttachments
                              attachmentList={msg?.attachment_urls?.filter(
                                (file) => file?.url && file?.name
                              )}
                            />
                          )}
                        <div className={styles["chat-time"]}>
                          {dayjs(msg?.sent_at).format("h:mm a")}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div className={styles["chat-date-label"]}>{values?.date}</div>
              </div>
            ))}
        </div>
        <div ref={bottomRef} />
      </div>

      <ChatInputSection
        disableChat={disableChat}
        bottomRef={bottomRef}
        onSend={onSend}
      />
    </div>
  );
};

export default ChatBox;
